import { createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import moment from 'moment';
import * as React from 'react';
import Alert from '../../components/Alert/Alert';
import WaBtn from '../../components/GeneralComponents/WaBtn';
import ACPKDebtsSummaryTable from '../../components/NegotiationDebts/ACPKDebtsSummaryTable';
import NewPaymentTermsContent from '../../components/PortalCajaSocial/NewPaymentTerms/NewPaymentTermsContent/NewPaymentTermsContent';
import { useSpace } from '../../content/utils/useSpace';
import useDictionary from '../../dictionaries/context/useDictionary';
import RenderText from '../../dictionaries/RenderText';
import { NegotiationOption, NegotiationOptionAlternative, NegotiationType, Normative026, OutDebt } from '../../generated/graphql';
import { getExtraN26Suffix } from '../../utils/getExtraN26Suffix';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { getNegotiationOptionLabel } from '../../utils/getNegotiationOptionLabel';
import { getNextPaymentDate } from '../../utils/getNextPaymentDate';
import { TranquiTheme } from '../App/theme';
import { useDebtNegExtraContext } from './DebtNegExtraContext';

interface DebtDetailsNegSumContentProps {
  negOption: NegotiationOption;
  outDebt: OutDebt;
  alternative?: NegotiationOptionAlternative;
}

const DebtDetailsNegotiationSummaryContent: React.FC<DebtDetailsNegSumContentProps> = (props) => {
  const { outDebt, alternative, negOption } = props;
  const { bank, productNumber, paymentDate, extra } = outDebt;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const negBooleans = getNegBooleans(outDebt, negOption, alternative);
  const {
    isNanocredit,
    isPaymentPromise,
    isPartialPaymentPromise,
    isBogota,
    isRefinance,
    isCatchUp,
    isMovistarMoney,
  } = negBooleans;
  const { gT } = useDictionary();
  const { info } = useDebtNegExtraContext();

  const isNoOptions = negOption.type === NegotiationType.NoOptions;
  const maxPaymentDate = info.paymentPromiseDate || negOption.settings?.maxPaymentDate;
  const n26Suffix = getExtraN26Suffix(outDebt, negOption);
  const partialPaymentPromiseSuffix = isPartialPaymentPromise ? '_partial' : '';

  // space implementation
  const spaceContext = {
    outDebt,
    option: negOption,
    alternative,
  };
  const summaryDescriptionSpace = useSpace('NEG_OPTION_SUMMARY', 'description', spaceContext);

  const optionLabel = getNegotiationOptionLabel(
    negOption.type,
    bank,
    extra?.normative026 as Normative026,
    alternative,
    outDebt.brand,
  );

  if (isNoOptions) {
    return (
      <>
        <Typography className={classes.noOptionsLabel}>
          <RenderText textKey="negotiation_NoOptions_label" />
        </Typography>

        <Typography className={classes.noOptionsLabel}>
          <RenderText textKey="negotiation_NoOptions_description" />
        </Typography>

        <WaBtn title={gT('action_wa_button_text_noOptions')} msg={gT('noOptions_wa_btn_message')} bank={bank} />
      </>
    );
  }

  return (
    <>
      {alternative
      && (
        <>
          <Typography
            className={classes.termsText}
            data-test="cy-summaryViewTitle-text"
          >
            <RenderText
              isMarkdown
              textKey="summaryView_title"
            />
          </Typography>
          <Typography
            className={cn(classes.termsText, 'mb-2')}
            data-test={`cy-${negOption.type}-resume-text`}
          >
            <RenderText
              textKey={summaryDescriptionSpace.keyIf || `negotiation_${negOption.type}${n26Suffix}_resumeText${partialPaymentPromiseSuffix}`}
              isMarkdown
              context={{ optionLabel }}
            />
          </Typography>
          <ACPKDebtsSummaryTable
            className="mb-2"
            debt={outDebt}
            withBackground
            title={gT('acpk_multi_table_title')}
          />

          <NewPaymentTermsContent
            outDebt={outDebt}
            negOption={negOption}
            alternative={alternative!}
            inSummary
          />
        </>
      )}
      {((isCatchUp && isMovistarMoney)
        || (isNanocredit && isPaymentPromise && isBogota && isRefinance)) && (
        <Alert
          color={gT(`alert_${negOption.type}_summary_color`) as any}
          title={gT(`alert_${negOption.type}_summary_title`)}
          filled={false}
          icon="info"
          iconColor={gT(`alert_${negOption.type}_summary_color`) as any}
          className={cn(classes.paymentAlert, 'mt-2')}
        >
          <RenderText
            isMarkdown
            textKey={`alert_${negOption.type}_summary_content${alternative?.type ? `_${alternative.type}` : ''}`}
            context={{
              negPaymentDate: moment(maxPaymentDate).format('ll'),
              productNumber: productNumber || '',
              paymentValue: alternative?.value || 0,
              negNextPaymentDate: getNextPaymentDate(paymentDate).format('ll'),
            }}
          />
        </Alert>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  termsText: {
    ...theme.typography.body2,
    color: theme.palette.text4.main,
    marginBottom: theme.spacing(1),
  },
  paymentAlert: {
    color: theme.palette.indigo.main,
    whiteSpace: 'pre-line',
  },
  noOptionsLabel: {
    ...theme.typography.h2,
    color: theme.palette.indigo.main,
    marginBottom: theme.spacing(2),
  },
  noOptionsDescription: {
    ...theme.typography.body1,
    color: theme.palette.text4.main,
    marginBottom: theme.spacing(2),
  },
}));
export default DebtDetailsNegotiationSummaryContent;
